import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { getSupabase, isSupabaseConfigured } from '@/lib/supabase';

/**
 * useAuth — anonymous session hook (Oturum 8 22:00 — yaşlı-dostu auth).
 *
 * Family eski email/Google/password auth'u tamamen kaldırıldı (Begüm UX
 * kararı). Tek mekanizma: `supabase.auth.signInAnonymously()`. Kullanıcı ad +
 * (varsa) aile kodu girer, arka planda anon user oluşur, Supabase auth.uid()
 * mevcut RLS + Edge Function altyapısı aynen çalışır.
 *
 * Trade-off (CLAUDE.md §15 D-revize): aile-içi düşük risk uygulama için
 * security relaxed kabul edildi. Email auth kalsa idi yaşlı kullanıcılar için
 * "login loop, OTP kodu gelmiyor, Google bug" kâbusu süreceği için tamamen
 * çıkarıldı. Trade-off: cihaz değişimi/temizleme → yeni anon user (mesaj
 * geçmişi DB'de kalır ama yeni cihazda görünmez).
 *
 * Returns the current Supabase session (or null) and a loading flag.
 * Subscribes to auth state changes (anon sign-in, sign-out) and refreshes.
 */

export interface AuthState {
  session: Session | null;
  loading: boolean;
}

export function useAuth(): AuthState {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }

    const supabase = getSupabase();
    let cancelled = false;

    void supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) {
        setSession(data.session);
        setLoading(false);
      }
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (!cancelled) setSession(newSession);
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  return { session, loading };
}

/**
 * Start a new anonymous session. Triggers `signInAnonymously()` — Supabase
 * creates a random UUID-only user (no email, no password). Session persists
 * in localStorage until signOut or browser clear.
 *
 * Used by Landing component when user taps "Yeni aile başlat" or "Aileme katıl".
 * After this resolves, App.tsx auth gate detects session and mounts Onboarding
 * (which reads localStorage flags `family.onboardingMode` and `family.pendingInviteCode`
 * to decide between create + claim flow).
 */
export async function startAnonSession(): Promise<{ ok: true } | { ok: false; error: string }> {
  if (!isSupabaseConfigured) {
    return { ok: false, error: 'setup.envFileMissing' };
  }
  const supabase = getSupabase();
  const { error } = await supabase.auth.signInAnonymously();
  if (error) {
    console.error('signInAnonymously', error.message);
    return { ok: false, error: 'auth.anonFailed' };
  }
  return { ok: true };
}

/**
 * Sign out — clears Supabase session + clears device-bound localStorage keys
 * (uiLang + onboardingComplete). Device-bound preferences (fontScale,
 * voiceMode, A2HS, pendingInviteCode, quietHours) survive intentionally per
 * CLAUDE.md device-pref principle.
 */
export async function signOut(): Promise<void> {
  if (!isSupabaseConfigured) return;
  const supabase = getSupabase();
  await supabase.auth.signOut();
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem('family.uiLang');
      localStorage.removeItem('family.onboardingComplete');
      localStorage.removeItem('family.onboardingMode');
      localStorage.removeItem('family.pendingInviteCode');
    } catch {
      // ignore (Safari private mode, quota, etc.)
    }
  }
}
